import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GameFrame, BetControls } from "@/components/GameFrame";
import { useAudio } from "@/context/GameFeel";

const BETS = [["red", "Red"], ["black", "Black"], ["even", "Even"], ["odd", "Odd"], ["low", "1-18"], ["high", "19-36"]];

export default function RouletteGame() {
  const { user, setUser } = useAuth();
  const { react, sfx } = useAudio();
  const [bet, setBet] = useState(100);
  const [type, setType] = useState("red");
  const [num, setNum] = useState(7);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);

  const spin = async () => {
    if (bet > (user?.coins ?? 0)) return toast.error("Not enough coins");
    setSpinning(true); setResult(null);
    try {
      const body = type === "number" ? { bet, bet_type: "number", bet_value: String(num) } : { bet, bet_type: type };
      const { data } = await api.post("/tables/roulette/spin", body);
      const ticks = setInterval(() => sfx("tick"), 120);
      await new Promise((r) => setTimeout(r, 900));
      clearInterval(ticks);
      setResult(data); setUser((u) => ({ ...u, coins: data.coins }));
      data.win ? toast.success(`${data.number} ${data.color} · +${data.payout.toLocaleString()}`) : toast(`${data.number} ${data.color} — no win`);
      react({ outcome: data.win ? "win" : "loss", game: "roulette", big: type === "number" && data.win });
    } catch (e) { toast.error(apiErr(e)); } finally { setSpinning(false); }
  };

  return (
    <GameFrame title="Roulette" subtitle="European wheel · coins only"
      side={<>
        <div className="glass rounded-2xl p-5 border border-white/10">
          <p className="text-sm text-gray-400 mb-2">Pick type</p>
          <div className="grid grid-cols-3 gap-2 mb-3">
            {BETS.map(([v, l]) => (
              <button key={v} onClick={() => setType(v)} data-testid={`roulette-bet-${v}`}
                className={`py-2 rounded-lg text-xs font-semibold ${type === v ? "bg-gradient-to-r from-amber-500 to-purple-600" : "bg-white/5 text-gray-400"}`}>{l}</button>
            ))}
          </div>
          <button onClick={() => setType("number")} className={`w-full py-2 rounded-lg text-xs font-semibold mb-2 ${type === "number" ? "bg-gradient-to-r from-amber-500 to-purple-600" : "bg-white/5 text-gray-400"}`}>Single Number (35:1)</button>
          {type === "number" && <input type="number" min={0} max={36} value={num} onChange={(e) => setNum(Math.max(0, Math.min(36, parseInt(e.target.value) || 0)))} className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 font-mono outline-none" />}
        </div>
        <BetControls bet={bet} setBet={setBet} coins={user?.coins} disabled={spinning} onPlay={spin} playLabel={spinning ? "Spinning…" : "SPIN"} testid="roulette-spin" />
      </>}>
      <div className="glass rounded-3xl p-10 border border-amber-500/20 glow-coins flex flex-col items-center">
        <motion.div animate={spinning ? { rotate: 360 } : {}} transition={{ repeat: spinning ? Infinity : 0, duration: 0.8, ease: "linear" }}
          className="w-40 h-40 rounded-full border-4 border-amber-500/40 flex items-center justify-center mb-6"
          style={{ background: "conic-gradient(#065f46 0 10deg, #7f1d1d 10deg 20deg, #111 20deg 30deg, #7f1d1d 30deg 40deg, #111 40deg 360deg)" }}>
          <span className="text-5xl font-mono font-black" style={{ color: result ? (result.color === "red" ? "#ef4444" : result.color === "green" ? "#10b981" : "#fff") : "#888" }}>
            {result ? result.number : "?"}
          </span>
        </motion.div>
        {result && <p className={`px-6 py-3 rounded-xl ${result.win ? "bg-emerald-500/15 text-emerald-300" : "bg-white/5 text-gray-400"}`}>{result.number} {result.color} · {result.win ? `+${result.payout.toLocaleString()} coins` : "No win"}</p>}
      </div>
    </GameFrame>
  );
}
